/**
 * Определение блокировщика рекламы у зрителя.
 *
 * Зачем: по событию `error` с кодом SRC_NOT_SUPPORTED не видно разницы
 * между битым креативом и запросом, который до сети вообще не дошёл. На
 * домене со словом `ads` в имени так терялось около 45% показов нашего
 * видеоформата, и понять это удалось только косвенно, сравнивая браузеры.
 * С этой проверкой признак приезжает прямо в событии.
 *
 * Как устроено: два запроса вместо одного. Приманка идёт на домен, который
 * блокировщики уже знают (там раньше лежали креативы, поэтому он есть в
 * фильтр-листах), контрольный на обычный домен той же CDN, с тем же
 * адресом и сертификатом. Разница в имени, а не в сети, поэтому:
 *
 *   приманка упала, контроль прошёл  → блокировщик
 *   оба прошли                        → блокировщика нет
 *   оба упали                         → нет сети, ничего не знаем
 *
 * Без контрольного запроса оффлайн и плохой мобильный интернет считались
 * бы блокировкой, и доля выходила бы завышенной.
 *
 * Проверка молчаливая: зритель ничего не видит и ни о чём не просит. Это
 * важно не только из вежливости. Видимые детекторы попадают в фильтр-листы
 * за недели, и вместе с ними туда уезжает домен, с которого мы отдаём
 * креативы.
 */

/** Домен, знакомый фильтр-листам: приманка. */
const BAIT_URL = "https://st.ads.prod.vdsprts.ru/";

/** Обычный домен той же CDN: контроль на случай, когда просто нет сети. */
const CONTROL_URL = "https://st.sp.prod.vdsprts.ru/";

/**
 * Блокировщик отвечает отказом сразу, поэтому ждать дольше нет смысла:
 * долгий ответ это признак плохой сети, а не фильтра.
 */
const TIMEOUT = 4000;

/** Результат проверки: true, false или undefined, если не определили. */
let state;

/** Проверка идёт один раз на страницу, остальные вызовы ждут тот же промис. */
let pending;

/**
 * Запрос, который нас интересует только фактом успеха. Ответ читать не
 * нужно и нельзя: `no-cors` отдаёт непрозрачный результат, у него нет ни
 * статуса, ни тела. Зато отказ на таком запросе означает ровно то, что
 * нужно: до сервера не дошли.
 */
function ping(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT);

  return fetch(url, {
    mode: "no-cors",
    cache: "no-store",
    credentials: "omit",
    redirect: "follow",
    signal: controller.signal,
  })
    .then(() => true)
    .catch(() => false)
    .finally(() => clearTimeout(timer));
}

/**
 * Запустить проверку. Возвращает промис с тем же значением, что потом
 * отдаёт getAdblockState.
 */
export function detectAdblock() {
  if (!process.browser) return Promise.resolve(undefined);
  if (pending) return pending;

  if (typeof fetch !== "function" || typeof AbortController !== "function") {
    return Promise.resolve(undefined);
  }

  pending = Promise.all([ping(BAIT_URL), ping(CONTROL_URL)]).then(
    ([bait, control]) => {
      // Контроль не прошёл: сети нет или CDN недоступна целиком, про
      // блокировщик в этом случае сказать нечего.
      state = control ? !bait : undefined;

      return state;
    }
  );

  return pending;
}

/**
 * Последний известный результат, синхронно. До того как проверка
 * завершится, и в случаях, когда она ничего не показала, возвращает
 * undefined: такие события в отчётах просто не попадут ни в одну из групп,
 * и это честнее, чем записать их к зрителям без блокировщика.
 */
export function getAdblockState() {
  return state;
}
